/**
 * @file KpiDetailModal.tsx
 * @description Generic click-through popup for a dashboard KPI card. Shows a
 * status breakdown plus the paginated list of underlying records for the
 * clicked KPI. Config-driven via kpiDrilldownConfig.ts so it doesn't need a
 * bespoke component per KPI.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { dashboardService, DashboardFilters, KpiDrilldownResult } from '../../services/dashboardService';
import { formatCount, formatDecimal, localeForCurrency } from '../../lib/formatNumber';
import { KPI_DRILLDOWN_CONFIG } from './kpiDrilldownConfig';
import { useCurrency } from '../../context/CurrencyContext';

const PAGE_SIZE = 10;

const BREAKDOWN_COLORS = [
  'bg-primary/10 text-primary border-primary/20',
  'bg-success/10 text-success border-success/20',
  'bg-warning/10 text-warning border-warning/25',
  'bg-destructive/10 text-destructive border-destructive/20',
  'bg-primary/10 text-primary border-primary/20',
  'bg-primary/10 text-primary border-primary/20',
];

const STATUS_BADGE_COLORS: Record<string, string> = {
  deployed: 'text-primary bg-primary/10 border-primary/20',
  active: 'text-success bg-success/10 border-success/20',
  assigned: 'text-primary bg-primary/10 border-primary/20',
  available: 'text-success bg-success/10 border-success/20',
  in_stock: 'text-success bg-success/10 border-success/20',
  overdue: 'text-destructive bg-destructive/10 border-destructive/20',
  disposed: 'text-muted-foreground bg-muted border-border',
  retired: 'text-muted-foreground bg-muted border-border',
  lost: 'text-destructive bg-destructive/10 border-destructive/20',
  stolen: 'text-destructive bg-destructive/10 border-destructive/20',
  poor: 'text-destructive bg-destructive/10 border-destructive/20',
  damaged: 'text-destructive bg-destructive/10 border-destructive/20',
  good: 'text-success bg-success/10 border-success/20',
  new: 'text-success bg-success/10 border-success/20',
  inactive: 'text-muted-foreground bg-muted border-border',
};

/** Breakdown labels come straight from raw enum/status values (`one_time`,
 * `deployed`) server-side — matches the underscore-stripping + capitalize
 * treatment the status column badges already get below, so the two don't
 * visibly disagree on how the same kind of value is written. */
const humanizeLabel = (label: string) => {
  const spaced = label.replace(/[_-]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const isDateKey = (key: string) => /date|expiry|createdAt|updatedAt/i.test(key);
const isStatusLikeKey = (key: string) => /^(status|condition|type)$/i.test(key);
const isMoneyKey = (key: string) => /cost|spend|rent|value|price/i.test(key);

const formatCellValue = (
  key: string,
  value: unknown,
  currencySymbol: string,
  currencyCode: string,
): React.ReactNode => {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;

  if (isStatusLikeKey(key) && typeof value === 'string') {
    const colorClass = STATUS_BADGE_COLORS[value.toLowerCase()] || 'text-foreground bg-muted border-border';
    return (
      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border capitalize ${colorClass}`}>
        {value.replace(/[_-]/g, ' ')}
      </span>
    );
  }

  if (isDateKey(key)) {
    const d = new Date(value as string);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }

  if ((typeof value === 'number' || (typeof value === 'string' && !Number.isNaN(Number(value)) && value !== '')) && isMoneyKey(key)) {
    const n = Number(value);
    if (!Number.isNaN(n)) {
      return <span className="tabular-nums">{currencySymbol}{formatDecimal(n, localeForCurrency(currencyCode))}</span>;
    }
  }

  return String(value);
};

interface KpiDetailModalProps {
  kpiId: string | null;
  onClose: () => void;
  /** Active page-level filters (date range, department, location, category) to scope the drilldown by. */
  filters?: DashboardFilters;
}

export const KpiDetailModal: React.FC<KpiDetailModalProps> = ({ kpiId, onClose, filters }) => {
  const { currencyCode, currencySymbol } = useCurrency();
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [data, setData] = useState<KpiDrilldownResult | null>(null);
  const [page, setPage] = useState(1);

  const config = kpiId ? KPI_DRILLDOWN_CONFIG[kpiId] : undefined;

  useEffect(() => {
    if (!kpiId) return;
    setPage(1);
  }, [kpiId]);

  useEffect(() => {
    if (!kpiId) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setStatus('loading');
    // Monetary columns (cost/spend/rent) are converted server-side into this
    // currency, same as the KPI cards themselves — see DashboardHome's loadDashboard.
    dashboardService.getKpiDrilldown(kpiId, { ...filters, page, pageSize: PAGE_SIZE, displayCurrency: currencyCode }, controller.signal)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setStatus('success');
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return; // superseded by a newer page/filter/kpiId change — not a real failure
        setStatus('error');
      });
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiId, page, currencyCode, JSON.stringify(filters)]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <Dialog open={!!kpiId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-[92vw] sm:max-w-3xl lg:max-w-5xl max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl flex items-center gap-2">
            {config?.title || 'KPI Details'}
            {status === 'success' && data && (
              <span className="text-sm font-normal text-muted-foreground">
                ({formatCount(data.total)} record{data.total !== 1 ? 's' : ''})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {status === 'loading' && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading details…
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-2 py-16 justify-center text-destructive">
              <AlertTriangle className="w-6 h-6" />
              <p className="text-sm">Couldn't load details for this KPI. Please try again.</p>
            </div>
          )}

          {status === 'success' && data && config && (
            <div className="space-y-5">
              {data.breakdown.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {data.breakdown.map((b, i) => (
                    <span
                      key={b.label}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length]}`}
                    >
                      {humanizeLabel(b.label)} <span className="tabular-nums">· {formatCount(b.value)}</span>
                    </span>
                  ))}
                </div>
              )}

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-muted/60 text-muted-foreground sticky top-0 z-10">
                      <tr>
                        {config.columns.map((col) => (
                          <th
                            key={col.key}
                            className="text-left font-semibold uppercase tracking-wide text-[11px] px-4 py-3 whitespace-nowrap border-b border-border"
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.length === 0 && (
                        <tr>
                          <td colSpan={config.columns.length} className="text-center py-12">
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                              <Inbox className="w-6 h-6" />
                              <span className="text-sm">No records found.</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {data.items.map((item, rowIdx) => (
                        <tr
                          key={item.id}
                          className={`border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors ${rowIdx % 2 === 1 ? 'bg-muted/20' : ''}`}
                        >
                          {config.columns.map((col) => (
                            <td key={col.key} className="px-4 py-3 whitespace-nowrap text-foreground">
                              {formatCellValue(col.key, (item as any)[col.key], currencySymbol, currencyCode)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {status === 'success' && data && data.total > 0 && (
          <div className="flex items-center justify-between px-6 py-3.5 border-t border-border shrink-0 text-sm">
            <span className="text-muted-foreground">
              Showing <span className="font-medium text-foreground">{(data.page - 1) * data.pageSize + 1}</span>
              –<span className="font-medium text-foreground">{Math.min(data.page * data.pageSize, data.total)}</span> of{' '}
              <span className="font-medium text-foreground">{formatCount(data.total)}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center gap-1 px-3 py-1.5 border border-border rounded-lg font-medium cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Previous
              </button>
              <span className="text-xs text-muted-foreground px-1">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex items-center gap-1 px-3 py-1.5 border border-border rounded-lg font-medium cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
